import { Text } from "@overmap-ai/blocks"
import { type ProcedureFieldValues, type Stored, UNLABELLED_ASSET_LABEL } from "@overmap-ai/common"
import { selectUserById, selectProcedureById, selectAssetById } from "@overmap-ai/core"
import { Link } from "@tanstack/react-router"
import { memo } from "react"

import { interactiveTextCva } from "../../cva"
import { ProjectTimelineItemType } from "../../enums/projects"
import { useAppSelector } from "../../store"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import { ProcedureTypeIcon } from "../Icons/ProcedureTypeIcon"
import { AssetIndexLabel } from "../IndexLabels/AssetIndexLabel"
import { ProcedureIndexLabel } from "../IndexLabels/ProcedureIndexLabel"
import { ProjectTimelineCard } from "./ProjectTimelineCard"
import { getTimelineDate } from "./utils"

interface ProjectTimelineProcedureFieldValuesItemProps {
	procedureFieldValues: Stored<ProcedureFieldValues>
}

export const ProjectTimelineProcedureFieldValuesItem = memo<ProjectTimelineProcedureFieldValuesItemProps>((props) => {
	const { procedureFieldValues } = props

	const user = useAppSelector(selectUserById(procedureFieldValues.created_by ?? -1))
	const procedure = useAppSelector(selectProcedureById(procedureFieldValues.procedure))!
	const asset = useAppSelector(selectAssetById(procedure?.asset ?? ""))!

	return (
		<ProjectTimelineCard type={ProjectTimelineItemType.PROCEDURE_FIELD_VALUES}>
			<Text size="sm" accentColor="base" className="min-w-0">
				<Text highContrast>{getUserDisplayLabel(user)}</Text>
				&nbsp;submitted field values for procedure&nbsp;
				<Link
					from="/$organizationId/$projectId"
					to="/$organizationId/$projectId/procedures/$procedureId"
					params={{ procedureId: procedure.uuid }}
				>
					<Text highContrast className={interactiveTextCva()}>
						<ProcedureTypeIcon className="align-sub" procedureTypeId={procedure.procedure_type} />
						&nbsp;
						<ProcedureIndexLabel procedureId={procedure.uuid} highContrast />
					</Text>
				</Link>
				<Text accentColor="base">
					&nbsp;&#8226;&nbsp;{formatCompactDate(getTimelineDate(procedureFieldValues))}
				</Text>
			</Text>

			<Text size="sm" accentColor="base" className="min-w-0">
				Context:&nbsp;
				<Link
					from="/$organizationId/$projectId"
					to="/$organizationId/$projectId/assets/$assetId"
					params={{ assetId: asset.uuid }}
				>
					<AssetTypeIcon className="align-sub" assetTypeId={asset.asset_type} />
					&nbsp;
					<Text className={interactiveTextCva()} accentColor="base">
						<AssetIndexLabel assetId={asset.uuid} highContrast />
						&nbsp;
						<Text>{asset.label || UNLABELLED_ASSET_LABEL}</Text>
					</Text>
				</Link>
			</Text>
		</ProjectTimelineCard>
	)
})
ProjectTimelineProcedureFieldValuesItem.displayName = "ProjectTimelineProcedureFieldValuesItem"
